import PT from 'prop-types';
import React from 'react';

import { Select } from '~/eds/src/components/Select/Select';
import { CountrySelect } from '~/eds/src/components/Select/subcomponents/CountrySelect';
import { CurrencySelect } from '~/eds/src/components/Select/subcomponents/CurrencySelect';
import { StateProvinceSelect } from '~/eds/src/components/Select/subcomponents/StateProvinceSelect';
import { WorkflowFieldDefaultValueInputType } from '~/enums/WorkflowFieldDefaultValueInputType';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import EmailInput from '~/ui/components/EmailInput/EmailInput';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import NumberInput from '~/ui/components/NumberInput/NumberInput';
import TextInput from '~/ui/components/TextInput/TextInput';

function DefaultValueInput({
  customSettings,
  defaultValue,
  disabled = false,
  id,
  isRequired = true,
  inputType = WorkflowFieldDefaultValueInputType.TextInput,
  label = undefined,
  options,
  placeholder,
  valueKey,
  width,
  defaultValueUpdateFactory,
  error,
}) {
  const intl = useIntl();

  const defaultLabel =
    label ??
    intl.formatMessage({
      id: 'DefaultValueInput.defaultLabel',
      defaultMessage: 'Default Value',
      description: 'Default label for value input',
    });

  const value = defaultValue[valueKey];
  const onChange = defaultValueUpdateFactory(valueKey);

  let Input;
  switch (inputType) {
    case WorkflowFieldDefaultValueInputType.CountrySingleSelect:
      Input = (
        <CountrySelect
          name="country-select"
          value={value || null}
          width={width}
          onChange={onChange}
          enablePortal
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.CurrencySingleSelect:
      Input = (
        <CurrencySelect
          name="currency-select"
          value={value || null}
          width={width}
          onChange={onChange}
          enablePortal
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.Email:
      Input = (
        <EmailInput
          placeholder={placeholder}
          width={width}
          value={value}
          onChange={onChange}
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.MultiSelect:
      Input = (
        <Select
          isMulti
          name="multi-select"
          options={options}
          placeholder={
            placeholder ||
            intl.formatMessage({
              id: 'DefaultValueInput.selectPlaceholder',
              defaultMessage: 'Select',
              description: 'Default placeholder for select inputs',
            })
          }
          value={value || null}
          width={width}
          onChange={onChange}
          enableErrorMessage
          error={error}
          enablePortal
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.NumberInput:
      Input = (
        <NumberInput
          placeholder={placeholder || '...'}
          value={value}
          width={width}
          onChange={onChange}
          allowDecimals={customSettings && !!customSettings.allow_decimals}
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.SingleSelect:
      Input = (
        <Select
          name="single-select"
          options={options}
          placeholder={
            placeholder ||
            intl.formatMessage({
              id: 'DefaultValueInput.selectPlaceholder',
              defaultMessage: 'Select',
              description: 'Default placeholder for select inputs',
            })
          }
          value={value || null}
          width={width}
          onChange={onChange}
          enableErrorMessage
          error={error}
          enablePortal
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.StateSingleSelect:
      Input = (
        <StateProvinceSelect
          country={defaultValue.country}
          name="state-single-select"
          value={value || null}
          width={width}
          onChange={onChange}
          enablePortal
        />
      );
      break;
    case WorkflowFieldDefaultValueInputType.TextInput:
      Input = (
        <TextInput
          placeholder={
            placeholder ||
            intl.formatMessage({
              id: 'DefaultValueInput.textPlaceholder',
              defaultMessage: 'Enter text',
              description: 'Default placeholder for text inputs',
            })
          }
          value={value || ''}
          width={width}
          onChange={onChange}
        />
      );
      break;
    default:
      break;
  }

  return (
    <InputLabel
      disabled={disabled}
      flex={width === 'fullWidth' ? '1 1 auto' : undefined}
      id={id}
      isRequired={isRequired}
      label={defaultLabel}
    >
      <Box sx={{ flex: '1 1 auto' }}>{Input}</Box>
    </InputLabel>
  );
}

DefaultValueInput.propTypes = {
  id: PT.string.isRequired,
  defaultValue: PT.object.isRequired,
  inputType: PT.oneOf(Object.values(WorkflowFieldDefaultValueInputType)),
  label: PT.string,
  options: PT.arrayOf(
    PT.shape({
      value: PT.any.isRequired,
      label: PT.string.isRequired,
    }).isRequired,
  ),
  valueKey: PT.string.isRequired,
  width: PT.string,
  defaultValueUpdateFactory: PT.func.isRequired,
  error: PT.string,
};
export default DefaultValueInput;
